/**
 * GlobalTimerWidget - Persistent floating widget when timer is running
 *
 * Shows across all pages when a timer is active.
 * Displays elapsed time and allows quick stop.
 */

import { useState, useEffect } from 'react';
import { Play, Square, Clock, X } from 'lucide-react';
import {
  useActiveTimer,
  useStopTimer,
  usePendingTimerSummaries,
  useDiscardPendingSummary,
  useConfirmPendingSummary,
  useElapsedTime,
  formatDuration,
} from '@business-assistant/shared';
import { useProjectsStore } from '@business-assistant/shared';
import { TimerStopForm } from './TimerStopForm';
import { useTranslation } from '../../hooks/useTranslation';
import './timer.css';

export function GlobalTimerWidget() {
  const { t } = useTranslation();
  const { data: activeTimer } = useActiveTimer();
  const stopTimerMutation = useStopTimer();
  const { data: pendingSummaries = [] } = usePendingTimerSummaries();
  const discardPendingSummaryMutation = useDiscardPendingSummary();
  const confirmPendingSummaryMutation = useConfirmPendingSummary();
  const { getProject, getTask, createTimeEntry } = useProjectsStore();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showStopForm, setShowStopForm] = useState(false);
  const [stoppedSummary, setStoppedSummary] = useState<typeof pendingSummaries[0] | null>(null);

  const stopTimer = async () => {
    const result = await stopTimerMutation.mutateAsync();
    return result;
  };

  const discardPendingSummary = async (summaryId: string) => {
    await discardPendingSummaryMutation.mutateAsync(summaryId);
  };

  const confirmPendingSummary = async (summaryId: string, data: { description: string; billable: boolean }) => {
    return await confirmPendingSummaryMutation.mutateAsync({ summaryId, ...data });
  };

  // Update elapsed time every second
  useEffect(() => {
    if (!activeTimer) {
      setElapsedSeconds(0);
      return;
    }

    const updateElapsed = () => {
      const start = new Date(activeTimer.startedAt).getTime();
      const now = Date.now();
      setElapsedSeconds(Math.floor((now - start) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleStop = async () => {
    const summary = await stopTimer();
    if (summary) {
      setStoppedSummary(summary);
      setShowStopForm(true);
    }
  };

  const handleConfirm = async (data: { description: string; billable: boolean }) => {
    if (!stoppedSummary) return;

    const entry = await confirmPendingSummary(stoppedSummary.id, data);
    if (entry) {
      // Add to project store
      createTimeEntry({
        projectId: stoppedSummary.projectId,
        taskId: stoppedSummary.taskId,
        userId: 'usr-current',
        userName: t('timer.user'),
        minutes: entry.minutes,
        description: entry.description,
        costRate: entry.costRate,
        billableRate: entry.billableRate,
        billable: entry.billable,
        at: entry.at,
      });
    }
    setShowStopForm(false);
    setStoppedSummary(null);
  };

  const handleDiscard = async () => {
    if (stoppedSummary) {
      await discardPendingSummary(stoppedSummary.id);
    }
    setShowStopForm(false);
    setStoppedSummary(null);
  };

  // Get task/project info
  const task = activeTimer ? getTask(activeTimer.taskId) : null;
  const project = activeTimer ? getProject(activeTimer.projectId) : null;

  // Don't render if no timer and no pending form
  if (!activeTimer && !showStopForm) return null;

  return (
    <>
      {/* Floating Widget */}
      {activeTimer && (
        <div className="global-timer-widget">
          <div className="global-timer-widget__icon">
            <Clock size={16} />
          </div>
          <div className="global-timer-widget__info">
            <div className="global-timer-widget__time">{formatTime(elapsedSeconds)}</div>
            <div className="global-timer-widget__task">
              {task?.title || 'Task'}
              {project && <span className="global-timer-widget__project"> • {project.title}</span>}
            </div>
          </div>
          <button
            className="global-timer-widget__stop"
            onClick={handleStop}
            title={t('timer.stopTimer')}
          >
            <Square size={14} />
          </button>
        </div>
      )}

      {/* Stop Form Modal */}
      {showStopForm && stoppedSummary && (
        <TimerStopForm
          summary={stoppedSummary}
          taskTitle={task?.title}
          projectTitle={project?.title}
          onConfirm={handleConfirm}
          onDiscard={handleDiscard}
        />
      )}
    </>
  );
}

export default GlobalTimerWidget;
